import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Line } from 'react-chartjs-2';
import { sans, mono, green, greenLight, greenDark, red, amber, amberLight, amberDark, coral, coralSoft, useTheme, useCurrency } from '../tokens';
import { fmtCompactC } from '../helpers';
import ScenarioChat from './ScenarioChat';
import MeetingsPanel from './MeetingsPanel';
import AnnualPLForecast from './AnnualPLForecast';
import { supabase as supabaseCommit } from '../supabaseClient';

// ─── Scenario presets ───────────────────────────────────────────────────────────
const SCENARIOS = [
  { key: 'base', label: 'Base Case', desc: 'Current trajectory — no changes', adjustments: {} },
  { key: 'aggressive-hire', label: 'Aggressive Hiring', desc: '+30% payroll from Q3 2026, +15 headcount', adjustments: { payroll: { startMonth: '2026-07', factor: 1.30 } } },
  { key: 'clinical-trial', label: 'Clinical Trial Start', desc: '+$500K/yr clinical spend starting 2027', adjustments: { clinical: { startYear: 2027, addPerYear: 500000 } } },
  { key: 'first-revenue', label: 'First Revenue', desc: 'Device sales starting Q1 2028 — $2M/yr growing 50%/yr', adjustments: { revenue: { startYear: 2028, initial: 2000000, growth: 0.50 } } },
  { key: 'conservative', label: 'Conservative', desc: 'Reduce burn 15%, extend runway', adjustments: { burnCut: { factor: 0.85 } } },
  { key: 'with-series-b', label: 'With Series B', desc: '$80M Series B raise in Q3 2027 — extends runway significantly', adjustments: { seriesB: { year: 2027, amount: 80000000 } } },
  { key: 'no-series-b', label: 'Without Series B', desc: 'No additional fundraise — runway based on current cash only', adjustments: {} },
];

// ─── Base forecast model ────────────────────────────────────────────────────────
function generateForecast(startYear, endYear, scenario) {
  const years = [];
  const adj = scenario?.adjustments || {};

  // Base annual figures (from real 2025 GL — annualized)
  const base = {
    interestIncome: 1952151,
    grants: 90992,
    payroll: 6875000,
    rdManufacturing: 3611264,
    rdClinical: 593617,
    professional: 1117002,
    facilities: 590694,
    softwareIT: 928297,
    hrPeople: 394344,
    depreciation: 753267,
    fxOther: 2478698,
  };

  const cashStart = 41106545; // total liquidity Dec 2025

  let cumulativeCash = cashStart;

  for (let y = startYear; y <= endYear; y++) {
    const yearFactor = 1 + (y - 2025) * 0.05; // 5% annual cost inflation

    let interest = Math.round(base.interestIncome * (cumulativeCash / cashStart) * 0.95);
    let grants = Math.round(base.grants * (y <= 2027 ? 1 : 0.5));
    let revenue = 0;
    let payroll = Math.round(base.payroll * yearFactor);
    let rdMfg = Math.round(base.rdManufacturing * yearFactor);
    let rdClinical = Math.round(base.rdClinical * (y >= 2027 ? 1.5 : 1) * yearFactor);
    let professional = Math.round(base.professional * yearFactor);
    let facilities = Math.round(base.facilities * yearFactor);
    let softwareIT = Math.round(base.softwareIT * yearFactor);
    let hrPeople = Math.round(base.hrPeople * yearFactor);
    let depreciation = Math.round(base.depreciation * yearFactor);
    let fxOther = Math.round(base.fxOther * 0.3); // normalize FX

    // Apply scenario adjustments
    if (adj.payroll && y >= parseInt(adj.payroll.startMonth?.slice(0, 4) || y)) {
      payroll = Math.round(payroll * adj.payroll.factor);
    }
    if (adj.clinical && y >= adj.clinical.startYear) {
      rdClinical += adj.clinical.addPerYear;
    }
    if (adj.revenue && y >= adj.revenue.startYear) {
      const yearsIn = y - adj.revenue.startYear;
      revenue = Math.round(adj.revenue.initial * Math.pow(1 + adj.revenue.growth, yearsIn));
    }
    if (adj.burnCut) {
      payroll = Math.round(payroll * adj.burnCut.factor);
      rdMfg = Math.round(rdMfg * adj.burnCut.factor);
      rdClinical = Math.round(rdClinical * adj.burnCut.factor);
      professional = Math.round(professional * adj.burnCut.factor);
    }

    // Series B fundraise
    let fundraise = 0;
    if (adj.seriesB && y === adj.seriesB.year) {
      fundraise = adj.seriesB.amount;
      cumulativeCash += fundraise;
      interest = Math.round(interest * 1.8); // more cash = more interest income
    }

    const totalIncome = interest + grants + revenue;
    const totalExpenses = payroll + rdMfg + rdClinical + professional + facilities + softwareIT + hrPeople + depreciation + fxOther;
    const netBurn = totalExpenses - totalIncome;
    cumulativeCash -= netBurn;

    years.push({
      year: y,
      interest, grants, revenue, fundraise, totalIncome: totalIncome + fundraise,
      payroll, rdMfg, rdClinical, professional, facilities, softwareIT, hrPeople, depreciation, fxOther,
      totalExpenses,
      netBurn,
      cashEnd: Math.max(0, cumulativeCash),
    });
  }

  return years;
}

// ─── KPI computation helper ──────────────────────────────────────────────────────
function computeScenarioKPIs(baseData, scenarioData) {
  const currentYear = new Date().getFullYear();
  const baseYearData = baseData.find(d => d.year === currentYear) || baseData[0];
  const scenarioYearData = scenarioData.find(d => d.year === currentYear) || scenarioData[0];

  const baseBurnMonthly = Math.round(baseYearData.netBurn / 12);
  const scenarioBurnMonthly = Math.round(scenarioYearData.netBurn / 12);
  const burnDelta = scenarioBurnMonthly - baseBurnMonthly;

  const baseRunway = baseBurnMonthly > 0 ? Math.round(baseYearData.cashEnd / baseBurnMonthly) : 999;
  const scenarioRunway = scenarioBurnMonthly > 0 ? Math.round(scenarioYearData.cashEnd / scenarioBurnMonthly) : 999;
  const runwayDelta = scenarioRunway - baseRunway;

  const baseCashEnd = baseYearData.cashEnd;
  const scenarioCashEnd = scenarioYearData.cashEnd;
  const cashDelta = scenarioCashEnd - baseCashEnd;

  const baseCashout = baseData.find(d => d.cashEnd <= 0)?.year || null;
  const scenarioCashout = scenarioData.find(d => d.cashEnd <= 0)?.year || null;

  return [
    {
      label: 'Monthly Burn',
      base: baseBurnMonthly, scenario: scenarioBurnMonthly,
      delta: burnDelta, positive: burnDelta <= 0,
    },
    {
      label: 'Cash Runway',
      base: baseRunway, scenario: scenarioRunway,
      delta: runwayDelta, positive: runwayDelta >= 0,
      isMo: true,
    },
    {
      label: `Cash End ${currentYear}`,
      base: baseCashEnd, scenario: scenarioCashEnd,
      delta: cashDelta, positive: cashDelta >= 0,
    },
    {
      label: 'Cash-out Year',
      base: baseCashout, scenario: scenarioCashout,
      delta: baseCashout && scenarioCashout ? scenarioCashout - baseCashout : 0,
      positive: !scenarioCashout || (baseCashout && scenarioCashout > baseCashout),
      isYear: true,
    },
  ];
}

// ─── Forecast Table ─────────────────────────────────────────────────────────────
const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function ForecastTable({ data, compareData }) {
  const t = useTheme();
  const cc = useCurrency();
  const [expandedYear, setExpandedYear] = useState(null);

  const rows = [
    { label: 'Payroll & Benefits', key: 'payroll' },
    { label: 'R&D — Manufacturing', key: 'rdMfg' },
    { label: 'R&D — Clinical', key: 'rdClinical' },
    { label: 'Professional & Legal', key: 'professional' },
    { label: 'Facilities', key: 'facilities' },
    { label: 'Software & IT', key: 'softwareIT' },
    { label: 'HR & People', key: 'hrPeople' },
    { label: 'Depreciation', key: 'depreciation' },
    { label: 'FX & Other', key: 'fxOther' },
    { label: 'Total Expenses', key: 'totalExpenses', isTotal: true },
    { label: '', key: 'spacer' },
    { label: 'Interest Income', key: 'interest', isRevenue: true },
    { label: 'Grants', key: 'grants', isRevenue: true },
    { label: 'Device Revenue', key: 'revenue', isRevenue: true },
    { label: 'Fundraise', key: 'fundraise', isRevenue: true },
    { label: 'Total Other Revenues', key: 'totalIncome', isTotal: true, isRevenue: true },
    { label: '', key: 'spacer2' },
    { label: 'Net Burn', key: 'netBurn', isTotal: true, isBurn: true },
    { label: 'Cash at Year-End', key: 'cashEnd', isTotal: true, isCash: true },
  ];

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, fontFamily: sans }}>
        <thead>
          <tr style={{ borderBottom: `2px solid ${t.border}` }}>
            <th style={{ textAlign: 'left', padding: '8px 10px', fontSize: 10, color: t.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, width: 180, position: 'sticky', left: 0, background: t.bgCard, zIndex: 1 }}>Category</th>
            {data.map(d => {
              const isExpanded = expandedYear === d.year;
              if (isExpanded) {
                return MONTH_LABELS.map((m, mi) => (
                  <th key={`${d.year}-${mi}`} style={{ textAlign: 'right', padding: '6px 6px', fontSize: 9, color: t.textMuted, fontWeight: 500, minWidth: 55 }}>
                    {mi === 0 && (
                      <div onClick={() => setExpandedYear(null)} style={{ cursor: 'pointer', fontSize: 10, color: green, fontWeight: 700, marginBottom: 2 }}>{d.year} &#x25C0;</div>
                    )}
                    {m}
                  </th>
                ));
              }
              return (
                <th key={d.year} onClick={() => !d.periodLabel && setExpandedYear(d.year)} style={{ textAlign: 'right', padding: '8px 10px', fontSize: 10, color: d.year <= 2025 ? green : t.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, cursor: d.periodLabel ? 'default' : 'pointer' }}>
                  {d.periodLabel || d.year}
                  {!d.periodLabel && d.year <= 2025 && <div style={{ fontSize: 8, color: green, fontWeight: 400 }}>Actual</div>}
                  {d.year > 2025 && <div style={{ fontSize: 8, color: t.textFaint, fontWeight: 400 }}>click to expand</div>}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => {
            if (row.key.startsWith('spacer')) return <tr key={ri}><td colSpan={data.length + 1} style={{ height: 8 }} /></tr>;
            return (
              <tr key={ri} style={{
                borderBottom: `1px solid ${row.isTotal ? t.border : t.borderLight}`,
                background: row.isTotal ? t.bgSurface : 'transparent',
              }}>
                <td style={{
                  padding: '6px 10px', fontWeight: row.isTotal ? 700 : 400,
                  color: row.isTotal ? t.textPrimary : t.textSecondary,
                  fontSize: row.isTotal ? 12 : 11,
                }}>{row.label}</td>
                {data.map(d => {
                  const val = d[row.key];
                  const compareVal = compareData?.find(c => c.year === d.year)?.[row.key];
                  const diff = compareVal != null ? val - compareVal : null;
                  let color = t.textPrimary;
                  if (row.isBurn) color = val > 0 ? red : green;
                  if (row.isCash) color = val < 5000000 ? red : val < 15000000 ? amber : green;
                  if (row.isRevenue && val > 0) color = green;

                  const isExpanded = expandedYear === d.year;
                  if (isExpanded) {
                    // Show 12 monthly cells (even split of annual)
                    const monthlyVal = val / 12;
                    return MONTH_LABELS.map((m, mi) => {
                      const mVal = row.key === 'fundraise' && val > 0 ? (mi === 6 ? val : 0) : // fundraise in Jul (Q3)
                                   row.key === 'cashEnd' ? Math.round(d.cashEnd - (d.netBurn / 12) * (11 - mi)) :
                                   Math.round(monthlyVal);
                      return (
                        <td key={`${d.year}-${mi}`} style={{ textAlign: 'right', padding: '4px 4px', fontFamily: mono, fontSize: 10, fontWeight: row.isTotal ? 600 : 400, color }}>
                          {mVal === 0 ? <span style={{ color: t.textFaint }}>—</span> : fmtCompactC(mVal, cc)}
                        </td>
                      );
                    });
                  }

                  return (
                    <td key={d.year} style={{ textAlign: 'right', padding: '6px 10px', fontFamily: mono, fontWeight: row.isTotal ? 700 : 400, color }}>
                      {val === 0 ? <span style={{ color: t.textFaint }}>—</span> : fmtCompactC(val, cc)}
                      {diff != null && diff !== 0 && (
                        <div style={{ fontSize: 9, color: diff > 0 ? (row.isRevenue ? green : red) : (row.isRevenue ? red : green) }}>
                          {diff > 0 ? '+' : ''}{fmtCompactC(diff, cc)}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Category keys for custom adjustments ───────────────────────────────────────
const CATEGORIES = [
  { key: 'payroll', label: 'Payroll & Benefits' },
  { key: 'rdMfg', label: 'R&D — Manufacturing' },
  { key: 'rdClinical', label: 'R&D — Clinical' },
  { key: 'professional', label: 'Professional & Legal' },
  { key: 'facilities', label: 'Facilities' },
  { key: 'softwareIT', label: 'Software & IT' },
  { key: 'hrPeople', label: 'HR & People' },
  { key: 'depreciation', label: 'Depreciation' },
  { key: 'interest', label: 'Interest Income' },
  { key: 'grants', label: 'Grants' },
  { key: 'revenue', label: 'Device Revenue' },
];

const ACTIONS = [
  { key: 'add', label: 'Add $/yr' },
  { key: 'reduce', label: 'Reduce $/yr' },
  { key: 'multiply', label: 'Multiply by %' },
];

function applyCustomAdjustments(data, customAdj) {
  if (!customAdj || customAdj.length === 0) return data;
  return data.map(yearData => {
    const d = { ...yearData };
    for (const adj of customAdj) {
      if (d.year < adj.startYear) continue;
      if (adj.action === 'add') d[adj.category] = (d[adj.category] || 0) + adj.amount;
      if (adj.action === 'reduce') d[adj.category] = Math.max(0, (d[adj.category] || 0) - adj.amount);
      if (adj.action === 'multiply') d[adj.category] = Math.round((d[adj.category] || 0) * (adj.amount / 100));
    }
    // Recalculate totals
    d.totalExpenses = d.payroll + d.rdMfg + d.rdClinical + d.professional + d.facilities + d.softwareIT + d.hrPeople + d.depreciation + d.fxOther;
    d.totalIncome = d.interest + d.grants + d.revenue + (d.fundraise || 0);
    d.netBurn = d.totalExpenses - d.totalIncome + (d.fundraise || 0);
    return d;
  });
}

// ─── Custom Adjustment Input Row ────────────────────────────────────────────────
function AdjustmentRow({ adj, onChange, onRemove, theme: t }) {
  const inputStyle = {
    fontSize: 12, fontFamily: sans, padding: '5px 8px', borderRadius: 6,
    border: `1px solid ${t.border}`, background: t.inputBg, color: t.textPrimary,
    outline: 'none', width: '100%',
  };
  const selectStyle = { ...inputStyle, appearance: 'none', cursor: 'pointer' };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px 100px 80px 28px', gap: 6, alignItems: 'center', marginBottom: 4 }}>
      <select value={adj.category} onChange={e => onChange({ ...adj, category: e.target.value })} style={selectStyle}>
        {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
      </select>
      <select value={adj.action} onChange={e => onChange({ ...adj, action: e.target.value })} style={selectStyle}>
        {ACTIONS.map(a => <option key={a.key} value={a.key}>{a.label}</option>)}
      </select>
      <input type="number" value={adj.amount} onChange={e => onChange({ ...adj, amount: Number(e.target.value) })}
        placeholder={adj.action === 'multiply' ? '120' : '500000'}
        style={{ ...inputStyle, fontFamily: mono }} />
      <select value={adj.startYear} onChange={e => onChange({ ...adj, startYear: Number(e.target.value) })} style={selectStyle}>
        {[2026, 2027, 2028, 2029, 2030, 2031].map(y => <option key={y} value={y}>{y}</option>)}
      </select>
      <button onClick={onRemove} style={{
        width: 24, height: 24, borderRadius: 12, border: `1px solid ${t.border}`,
        background: 'transparent', color: t.textMuted, cursor: 'pointer', fontSize: 12,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>x</button>
    </div>
  );
}

// ─── Main Forecast View ─────────────────────────────────────────────────────────
export default function ForecastView({ mode = 'actual' }) {
  const t = useTheme();
  const cc = useCurrency();
  const [forecastMode, setForecastMode] = useState(mode); // 'actual', 'scenario', 'commitments'
  // Keep in sync if parent changes mode (e.g. switching subtabs)
  useEffect(() => {
    setForecastMode(mode);
    setShowScenarioPanel(mode === 'scenario');
  }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps
  const [selectedScenario, setSelectedScenario] = useState('base');
  const [showScenarioPanel, setShowScenarioPanel] = useState(false);
  const [customAdjustments, setCustomAdjustments] = useState([]);
  const [noteText, setNoteText] = useState('');
  // Accepted forecast: stored scenario key (persisted in localStorage)
  const [acceptedScenario, setAcceptedScenario] = useState(() => localStorage.getItem('closely_accepted_forecast') || 'base');

  // Saved scenarios
  const [savedScenarios, setSavedScenarios] = useState(() => {
    try { return JSON.parse(localStorage.getItem('finkloz_saved_scenarios')) || []; } catch { return []; }
  });
  const [activeScenarioId, setActiveScenarioId] = useState(null);
  const [savingName, setSavingName] = useState(null); // null = not saving, string = editing name
  const [savingMonth, setSavingMonth] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; });
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [approvedCommitments, setApprovedCommitments] = useState([]);
  const [selectedCommitmentIds, setSelectedCommitmentIds] = useState(new Set());
  const [pendingCommitments, setPendingCommitments] = useState([]);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [reviewingCommitment, setReviewingCommitment] = useState(null);
  const [reviewGLAccount, setReviewGLAccount] = useState('');
  const [reviewRejectReason, setReviewRejectReason] = useState('');
  const [reviewRejecting, setReviewRejecting] = useState(false);

  // Meetings mini-widget
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [meetingProposals, setMeetingProposals] = useState([]);
  const [meetingMode, setMeetingMode] = useState('list'); // 'list' | 'propose' | 'book'
  const [meetingSaving, setMeetingSaving] = useState(false);
  const [meetingGoogleToken, setMeetingGoogleToken] = useState(null);
  const [meetingGoogleUser, setMeetingGoogleUser] = useState(null);
  const [meetingFreeSlots, setMeetingFreeSlots] = useState([]);
  const [meetingLoadingSlots, setMeetingLoadingSlots] = useState(false);
  const [meetingSelectedSlot, setMeetingSelectedSlot] = useState(null);
  const [meetingBookDate, setMeetingBookDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [meetingBookDuration, setMeetingBookDuration] = useState(30);
  const MEETING_DEPTS = ['Engineering', 'Clinical', 'Operations', 'Finance', 'Regulatory', 'Executive', 'Machine Shop & Metrology'];
  const EMPTY_MEET_FORM = { title: '', department: 'Engineering', leadName: '', leadEmail: '', duration: 30, context: '', slots: [{ date: '', time: '' }, { date: '', time: '' }, { date: '', time: '' }] };
  const [meetingForm, setMeetingForm] = useState(EMPTY_MEET_FORM);
  const [meetingBookForm, setMeetingBookForm] = useState({ title: '', department: 'Engineering', leadName: '', leadEmail: '', context: '' });
  const [forecastPeriod, setForecastPeriod] = useState('full'); // 'mtd', 'ytd', 'full'
  const [expandedMonths, setExpandedMonths] = useState(() => {
    const d = new Date();
    return [`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`];
  });

  // Fetch commitments when entering scenario mode
  const fetchScenarioCommitments = useCallback(async () => {
    const { data } = await supabaseCommit.from('commitments').select('*').in('status', ['confirmed', 'committed']).order('created_at', { ascending: false });
    if (data) {
      setApprovedCommitments(data.filter(c => c.status === 'committed').map(c => ({
        id: c.id, desc: c.description, vendor: c.vendor || '—', dept: c.dept || '',
        amount: Number(c.amount), date: c.expected_date,
        glAccount: c.gl_account || null, forecastCategory: c.forecast_category || null,
      })));
      setPendingCommitments(data.filter(c => c.status === 'confirmed').map(c => ({
        id: c.id, desc: c.description, vendor: c.vendor || '—', dept: c.dept || '',
        amount: Number(c.amount), date: c.expected_date, createdBy: c.created_by_name || 'Unknown',
        recurrence: c.recurrence || '—', glAccount: c.gl_account || '',
      })));
    }
  }, []);

  useEffect(() => {
    if (forecastMode !== 'scenario') return;
    fetchScenarioCommitments();
    const ch = supabaseCommit.channel('commitments-scenario')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'commitments' }, fetchScenarioCommitments)
      .subscribe();
    return () => supabaseCommit.removeChannel(ch);
  }, [forecastMode, fetchScenarioCommitments]);

  const handleApproveCommitment = async (id, glAccount) => {
    const updates = { status: 'committed', approved_at: new Date().toISOString() };
    if (glAccount) updates.gl_account = glAccount;
    await supabaseCommit.from('commitments').update(updates).eq('id', id);
    setReviewingCommitment(null); setReviewGLAccount(''); setReviewRejecting(false); setReviewRejectReason('');
    fetchScenarioCommitments();
  };
  const handleRejectCommitment = async (id, reason) => {
    await supabaseCommit.from('commitments').update({ status: 'rejected', rejection_reason: reason || '' }).eq('id', id);
    setRejectingId(null); setRejectReason('');
    setReviewingCommitment(null); setReviewGLAccount(''); setReviewRejecting(false); setReviewRejectReason('');
    fetchScenarioCommitments();
  };

  // ─── Meetings mini-widget functions ──────────────────────────────────────────
  const fetchMeetingProposals = useCallback(async () => {
    const { data } = await supabaseCommit.from('meeting_proposals').select('*').order('created_at', { ascending: false });
    if (data) setMeetingProposals(data);
  }, []);

  useEffect(() => {
    if (forecastMode !== 'scenario') return;
    fetchMeetingProposals();
  }, [forecastMode, fetchMeetingProposals]);

  const meetingSignInGoogle = () => {
    if (!window.google) { alert('Google SDK loading… try again.'); return; }
    const client = window.google.accounts.oauth2.initTokenClient({
      client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
      scope: 'https://www.googleapis.com/auth/calendar.events https://www.googleapis.com/auth/calendar.readonly https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile',
      callback: async (resp) => {
        if (resp.access_token) {
          setMeetingGoogleToken(resp.access_token);
          const res = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', { headers: { Authorization: `Bearer ${resp.access_token}` } });
          setMeetingGoogleUser(await res.json());
        }
      },
    });
    client.requestAccessToken();
  };

  const fetchMeetingFreeSlots = async (date, duration, token) => {
    setMeetingLoadingSlots(true); setMeetingFreeSlots([]); setMeetingSelectedSlot(null);
    try {
      const res = await fetch('https://www.googleapis.com/calendar/v3/freeBusy', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ timeMin: new Date(date + 'T00:00:00').toISOString(), timeMax: new Date(date + 'T23:59:59').toISOString(), items: [{ id: 'primary' }] }),
      });
      const d = await res.json();
      const busy = d.calendars?.primary?.busy || [];
      const step = 30; const slots = [];
      const cur = new Date(date + 'T08:00:00');
      const end = new Date(date + 'T18:00:00');
      while (cur < end) {
        const s = new Date(cur); const e = new Date(cur.getTime() + duration * 60000);
        if (e <= end && !busy.some(b => s < new Date(b.end) && e > new Date(b.start))) slots.push({ start: s.toISOString(), end: e.toISOString() });
        cur.setMinutes(cur.getMinutes() + step);
      }
      setMeetingFreeSlots(slots);
    } catch(err) { alert('Calendar error: ' + err.message); }
    setMeetingLoadingSlots(false);
  };

  const meetingBookDirectly = async () => {
    if (!meetingSelectedSlot || !meetingBookForm.title.trim() || !meetingBookForm.leadName.trim()) { alert('Fill in title, lead name, and select a slot.'); return; }
    setMeetingSaving(true);
    try {
      const ev = { summary: `${meetingBookForm.title.trim()} — FinKloz`, description: `Dept: ${meetingBookForm.department}\n${meetingBookForm.context}`, start: { dateTime: meetingSelectedSlot.start, timeZone: 'America/Montreal' }, end: { dateTime: meetingSelectedSlot.end, timeZone: 'America/Montreal' }, attendees: meetingBookForm.leadEmail?.includes('@') ? [{ email: meetingBookForm.leadEmail }] : [], sendUpdates: 'all' };
      const res = await fetch('https://www.googleapis.com/calendar/v3/calendars/primary/events', { method: 'POST', headers: { Authorization: `Bearer ${meetingGoogleToken}`, 'Content-Type': 'application/json' }, body: JSON.stringify(ev) });
      const gcal = await res.json();
      if (!gcal.id) { alert('GCal error: ' + (gcal.error?.message || '')); setMeetingSaving(false); return; }
      await supabaseCommit.from('meeting_proposals').insert({ title: meetingBookForm.title.trim(), department: meetingBookForm.department, lead_name: meetingBookForm.leadName.trim(), lead_email: meetingBookForm.leadEmail.trim(), duration: meetingBookDuration, context: meetingBookForm.context.trim(), proposed_by: meetingGoogleUser?.name || 'Finance', proposed_by_email: meetingGoogleUser?.email || '', proposed_slots: [meetingSelectedSlot], selected_slot: meetingSelectedSlot, google_event_id: gcal.id, status: 'booked' });
      setMeetingMode('list'); setMeetingSelectedSlot(null); setMeetingBookForm({ title: '', department: 'Engineering', leadName: '', leadEmail: '', context: '' });
      fetchMeetingProposals();
    } catch(e) { alert('Error: ' + e.message); }
    setMeetingSaving(false);
  };

  const meetingSubmitProposal = async () => {
    const validSlots = meetingForm.slots.filter(s => s.date && s.time).map(s => { const dt = new Date(`${s.date}T${s.time}:00`); return { start: dt.toISOString(), end: new Date(dt.getTime() + meetingForm.duration * 60000).toISOString() }; });
    if (!meetingForm.title.trim() || !meetingForm.leadName.trim() || validSlots.length === 0) { alert('Fill in title, lead name, and at least one slot.'); return; }
    setMeetingSaving(true);
    try {
      await supabaseCommit.from('meeting_proposals').insert({ title: meetingForm.title.trim(), department: meetingForm.department, lead_name: meetingForm.leadName.trim(), lead_email: meetingForm.leadEmail.trim(), duration: meetingForm.duration, context: meetingForm.context.trim(), proposed_by: meetingGoogleUser?.name || 'Finance', proposed_by_email: meetingGoogleUser?.email || '', proposed_slots: validSlots, status: 'pending' });
      setMeetingMode('list'); setMeetingForm(EMPTY_MEET_FORM);
      fetchMeetingProposals();
    } catch(e) { alert('Error: ' + e.message); }
    setMeetingSaving(false);
  };

  const fmtMeetSlot = (iso) => { const d = new Date(iso); return d.toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' }) + ' · ' + d.toLocaleTimeString('en-CA', { hour: '2-digit', minute: '2-digit' }); };
  const MEET_STATUS = { pending: { bg: amberLight, text: amberDark, label: 'Pending' }, confirmed: { bg: greenLight, text: greenDark, label: 'Confirmed' }, booked: { bg: greenLight, text: greenDark, label: 'Booked' }, calendar_created: { bg: '#e8f5e9', text: '#2e7d32', label: 'Invite Sent' }, declined: { bg: '#fce4e4', text: red, label: 'Declined' } };

  const persistScenarios = (scenarios) => {
    setSavedScenarios(scenarios);
    localStorage.setItem('finkloz_saved_scenarios', JSON.stringify(scenarios));
  };

  const getNextName = () => {
    const nums = savedScenarios.map(s => { const m = s.name.match(/^Forecast (\d+)$/); return m ? parseInt(m[1]) : 0; });
    return `Forecast ${Math.max(0, ...nums) + 1}`;
  };

  const saveScenario = (name) => {
    if (activeScenarioId) {
      // Update existing
      persistScenarios(savedScenarios.map(s => s.id === activeScenarioId ? { ...s, name: name || s.name, adjustments: [...customAdjustments] } : s));
    } else {
      // Create new
      const id = Date.now().toString(36);
      const now = new Date();
      const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      const newScenario = { id, name: name || getNextName(), month: currentMonth, adjustments: [...customAdjustments], createdAt: now.toISOString() };
      persistScenarios([...savedScenarios, newScenario]);
      setActiveScenarioId(id);
    }
    setSavingName(null);
  };

  // Get unique months from saved scenarios
  const savedMonths = [...new Set(savedScenarios.map(s => s.month || 'unknown'))].sort().reverse();

  const toggleMonth = (m) => {
    setExpandedMonths(prev => prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m]);
  };

  const formatMonth = (m) => {
    if (!m || m === 'unknown') return 'Other';
    const [y, mo] = m.split('-');
    const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${names[parseInt(mo) - 1]} ${y}`;
  };

  // Generate month options for dropdown (12 months of current year)
  const monthOptions = (() => {
    const year = new Date().getFullYear();
    return Array.from({ length: 12 }, (_, i) => `${year}-${String(i + 1).padStart(2, '0')}`);
  })();

  const loadScenario = (id) => {
    const s = savedScenarios.find(s => s.id === id);
    if (s) {
      setCustomAdjustments([...s.adjustments]);
      setActiveScenarioId(id);
    }
  };

  const deleteScenario = (id) => {
    persistScenarios(savedScenarios.filter(s => s.id !== id));
    if (activeScenarioId === id) {
      setActiveScenarioId(null);
      setCustomAdjustments([]);
    }
  };

  const renameScenario = (id, newName) => {
    if (!newName.trim()) return;
    persistScenarios(savedScenarios.map(s => s.id === id ? { ...s, name: newName.trim() } : s));
    setRenamingId(null);
  };

  const scenario = SCENARIOS.find(s => s.key === selectedScenario);
  const acceptedDef = SCENARIOS.find(s => s.key === acceptedScenario) || SCENARIOS[0];
  const baseData = useMemo(() => generateForecast(2025, 2031, acceptedDef), [acceptedDef]);
  const rawScenarioData = useMemo(() => generateForecast(2025, 2031, scenario), [scenario]);
  const scenarioDataWithAdj = useMemo(() => applyCustomAdjustments(rawScenarioData, customAdjustments), [rawScenarioData, customAdjustments]);
  const scenarioData = useMemo(() => {
    const selected = approvedCommitments.filter(c => selectedCommitmentIds.has(c.id));
    if (selected.length === 0) return scenarioDataWithAdj;
    // Group selected commitment amounts by year, split by target forecast line
    // Structure: { year: { category: amount } }
    const byYear = {};
    for (const c of selected) {
      const y = c.date ? parseInt(c.date.slice(0, 4)) : null;
      const cat = c.forecastCategory || 'fxOther';
      if (y) {
        if (!byYear[y]) byYear[y] = {};
        byYear[y][cat] = (byYear[y][cat] || 0) + c.amount;
      }
    }
    // Apply extra expenses with cumulative cashEnd cascade
    let cumulativeExtra = 0;
    return scenarioDataWithAdj.map(d => {
      const yearCats = byYear[d.year] || {};
      const extra = Object.values(yearCats).reduce((s, v) => s + v, 0);
      cumulativeExtra += extra;
      if (extra === 0 && cumulativeExtra === 0) return d;
      const updated = { ...d };
      for (const [cat, amt] of Object.entries(yearCats)) {
        updated[cat] = (updated[cat] || 0) + amt;
      }
      updated.totalExpenses = d.totalExpenses + extra;
      updated.netBurn = d.netBurn + extra;
      updated.cashEnd = Math.max(0, d.cashEnd - cumulativeExtra);
      return updated;
    });
  }, [scenarioDataWithAdj, selectedCommitmentIds, approvedCommitments]);

  const addAdjustment = () => {
    setCustomAdjustments(prev => [...prev, { category: 'payroll', action: 'add', amount: 0, startYear: 2027 }]);
  };

  const updateAdjustment = (idx, adj) => {
    setCustomAdjustments(prev => prev.map((a, i) => i === idx ? adj : a));
  };

  const removeAdjustment = (idx) => {
    setCustomAdjustments(prev => prev.filter((_, i) => i !== idx));
  };

  const isComparing = forecastMode === 'scenario' && showScenarioPanel && (selectedScenario !== acceptedScenario || customAdjustments.length > 0);
  const scenarioLabel = customAdjustments.length > 0
    ? `${scenario.label} + ${customAdjustments.length} adjustment${customAdjustments.length > 1 ? 's' : ''}`
    : scenario.label;

  // Active data: base forecast in actual mode, scenario data in scenario mode
  const fullDisplayData = forecastMode === 'actual' ? baseData : scenarioData;

  // Filter data based on period toggle (MTD/YTD/Full)
  const displayData = useMemo(() => {
    if (forecastMode !== 'actual' || forecastPeriod === 'full') return fullDisplayData;

    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth(); // 0-indexed

    if (forecastPeriod === 'mtd') {
      // Show only current year with monthly breakdown for current month only
      const yearData = fullDisplayData.find(d => d.year === currentYear);
      if (!yearData) return fullDisplayData;
      const monthlyData = yearData.monthly || [];
      const currentMonthData = monthlyData[currentMonth];
      if (!currentMonthData) return [yearData];
      // Return a single-item array with just the current month's data prorated
      return [{
        ...yearData,
        year: currentYear,
        periodLabel: MONTH_LABELS[currentMonth] + ' ' + currentYear,
        payroll: currentMonthData.payroll || Math.round(yearData.payroll / 12),
        rdMfg: currentMonthData.rdMfg || Math.round(yearData.rdMfg / 12),
        rdClinical: currentMonthData.rdClinical || Math.round(yearData.rdClinical / 12),
        professional: currentMonthData.professional || Math.round(yearData.professional / 12),
        facilities: currentMonthData.facilities || Math.round(yearData.facilities / 12),
        softwareIT: currentMonthData.softwareIT || Math.round(yearData.softwareIT / 12),
        hrPeople: currentMonthData.hrPeople || Math.round(yearData.hrPeople / 12),
        depreciation: currentMonthData.depreciation || Math.round(yearData.depreciation / 12),
        fxOther: currentMonthData.fxOther || Math.round(yearData.fxOther / 12),
        interest: currentMonthData.interest || Math.round(yearData.interest / 12),
        grants: currentMonthData.grants || Math.round(yearData.grants / 12),
        revenue: currentMonthData.revenue || Math.round(yearData.revenue / 12),
        fundraise: 0,
        totalExpenses: Math.round(yearData.totalExpenses / 12),
        totalIncome: Math.round(yearData.totalIncome / 12),
        netBurn: Math.round(yearData.netBurn / 12),
        cashEnd: yearData.cashEnd,
      }];
    }

    if (forecastPeriod === 'ytd') {
      // Show current year prorated to months elapsed
      const yearData = fullDisplayData.find(d => d.year === currentYear);
      if (!yearData) return fullDisplayData;
      const monthsElapsed = currentMonth + 1;
      const factor = monthsElapsed / 12;
      return [{
        ...yearData,
        year: currentYear,
        periodLabel: `Jan–${MONTH_LABELS[currentMonth]} ${currentYear}`,
        payroll: Math.round(yearData.payroll * factor),
        rdMfg: Math.round(yearData.rdMfg * factor),
        rdClinical: Math.round(yearData.rdClinical * factor),
        professional: Math.round(yearData.professional * factor),
        facilities: Math.round(yearData.facilities * factor),
        softwareIT: Math.round(yearData.softwareIT * factor),
        hrPeople: Math.round(yearData.hrPeople * factor),
        depreciation: Math.round(yearData.depreciation * factor),
        fxOther: Math.round(yearData.fxOther * factor),
        interest: Math.round(yearData.interest * factor),
        grants: Math.round(yearData.grants * factor),
        revenue: Math.round(yearData.revenue * factor),
        fundraise: Math.round((yearData.fundraise || 0) * factor),
        totalExpenses: Math.round(yearData.totalExpenses * factor),
        totalIncome: Math.round(yearData.totalIncome * factor),
        netBurn: Math.round(yearData.netBurn * factor),
        cashEnd: yearData.cashEnd,
      }];
    }

    return fullDisplayData;
  }, [fullDisplayData, forecastMode, forecastPeriod]);

  // Cash runway chart data — always uses full data for the chart
  const chartSourceData = forecastMode === 'actual' ? baseData : scenarioData;
  const chartData = {
    labels: chartSourceData.map(d => d.year),
    datasets: [
      ...(isComparing ? [{
        label: `Accepted (${acceptedDef.label})`,
        data: baseData.map(d => d.cashEnd),
        borderColor: t.textMuted,
        borderDash: [6, 4],
        backgroundColor: 'transparent',
        tension: 0.3,
        pointRadius: 3,
        pointBackgroundColor: t.textMuted,
      }] : []),
      {
        label: isComparing ? scenarioLabel : `${acceptedDef.label} (Accepted)`,
        data: chartSourceData.map(d => d.cashEnd),
        borderColor: chartSourceData.some(d => d.cashEnd < 5000000) ? red : green,
        backgroundColor: (chartSourceData.some(d => d.cashEnd < 5000000) ? red : green) + '12',
        fill: true,
        tension: 0.3,
        pointRadius: 5,
        pointBackgroundColor: chartSourceData.map(d => d.cashEnd < 5000000 ? red : d.cashEnd < 15000000 ? amber : green),
      },
    ],
  };

  // Find when cash runs out
  const cashoutYear = chartSourceData.find(d => d.cashEnd <= 0)?.year;
  const lowestCash = Math.min(...chartSourceData.map(d => d.cashEnd));

  const approveScenario = () => {
    setAcceptedScenario(selectedScenario);
    localStorage.setItem('closely_accepted_forecast', selectedScenario);
    setShowScenarioPanel(false);
  };

  return (
    <div>
      {/* Mode pills moved to left nav (FP&A subtabs) */}

      {/* Accepted Forecast header strip + MTD/YTD toggle — removed per user */}
      {/* Saved Scenarios Bar — 2 column layout */}
      {forecastMode === 'scenario' && (
        <>
        {/* Meetings widget at top */}
        <div style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 10, padding: '12px 16px', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: meetingProposals.length > 0 ? 10 : 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: t.textPrimary, fontFamily: sans }}>Meetings</span>
              {meetingProposals.length > 0 && <span style={{ fontSize: 10, background: t.bgSurface, color: t.textMuted, borderRadius: 10, padding: '1px 8px', fontWeight: 600, fontFamily: sans }}>{meetingProposals.length}</span>}
            </div>
            <button onClick={() => { setShowMeetingModal(true); setMeetingMode('list'); fetchMeetingProposals(); }}
              style={{ padding: '4px 12px', borderRadius: 6, border: `1px solid ${green}`, background: 'transparent', color: green, fontSize: 10, fontWeight: 600, cursor: 'pointer', fontFamily: sans }}>
              + Schedule Meeting
            </button>
          </div>
          {meetingProposals.slice(0, 3).map(p => {
            const sc = MEET_STATUS[p.status] || MEET_STATUS.pending;
            return (
              <div key={p.id} onClick={() => { setShowMeetingModal(true); setMeetingMode('list'); }} style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 6, borderTop: `1px solid ${t.borderLight}`, cursor: 'pointer' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: t.textPrimary, fontFamily: sans }}>{p.title}</div>
                  <div style={{ fontSize: 9, color: t.textMuted, fontFamily: sans }}>{p.department} · {p.lead_name}</div>
                </div>
                <span style={{ fontSize: 9, fontWeight: 600, background: sc.bg, color: sc.text, borderRadius: 8, padding: '2px 8px', fontFamily: sans }}>{sc.label}</span>
              </div>
            );
          })}
        </div>
        <div style={{
          display: 'flex', gap: 12, marginBottom: 12,
        }}>
          {/* LEFT: scenario list */}
          <div style={{
            flex: '0 0 38%', background: t.bgSurface, border: `1px solid ${t.border}`, borderRadius: 10,
            padding: '12px 16px',
          }}>
            {/* Top row: title + actions */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: savedScenarios.length > 0 || savingName !== null ? 10 : 0 }}>
              <span style={{ fontSize: 10, fontWeight: 600, color: t.textMuted, fontFamily: sans }}>Saved Scenario Forecasts</span>
              <div style={{ display: 'flex', gap: 6 }}>
                {activeScenarioId && customAdjustments.length > 0 && (
                  <button onClick={() => {
                    persistScenarios(savedScenarios.map(s => s.id === activeScenarioId ? { ...s, adjustments: [...customAdjustments] } : s));
                  }} style={{
                    padding: '4px 10px', borderRadius: 14, fontSize: 10, fontFamily: sans,
                    border: `1.5px solid ${green}60`, background: greenLight,
                    color: greenDark, cursor: 'pointer', fontWeight: 500,
                  }}>
                    Save
                  </button>
                )}
                <button onClick={() => {
                  setActiveScenarioId(null);
                  setCustomAdjustments([]);
                  setSavingName(getNextName());
                }} style={{
                  padding: '4px 10px', borderRadius: 14, fontSize: 10, fontFamily: sans,
                  border: `1.5px dashed ${t.border}`, background: 'transparent',
                  color: t.textSecondary, cursor: 'pointer', fontWeight: 500,
                }}>
                  + New
                </button>
              </div>
            </div>

            {/* Save form */}
            {savingName !== null && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                <input
                  autoFocus
                  value={savingName}
                  onChange={e => setSavingName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') saveScenario(savingName); if (e.key === 'Escape') setSavingName(null); }}
                  placeholder={getNextName()}
                  style={{
                    border: `1px solid ${t.border}`, borderRadius: 6, padding: '4px 10px',
                    fontSize: 11, fontFamily: sans, flex: 1, outline: 'none',
                    background: t.inputBg, color: t.textPrimary,
                  }}
                />
                <button onClick={() => saveScenario(savingName || getNextName())} style={{
                  padding: '4px 12px', borderRadius: 6, border: 'none',
                  background: green, color: '#fff', fontSize: 10, fontWeight: 600,
                  fontFamily: sans, cursor: 'pointer',
                }}>Save</button>
                <button onClick={() => setSavingName(null)} style={{
                  padding: '4px 8px', borderRadius: 6, border: `1px solid ${t.border}`,
                  background: 'transparent', fontSize: 10, color: t.textMuted,
                  fontFamily: sans, cursor: 'pointer',
                }}>Cancel</button>
              </div>
            )}

            {/* Monthly sections */}
            {savedMonths.map(month => {
              const monthScenarios = savedScenarios.filter(s => (s.month || 'unknown') === month);
              if (monthScenarios.length === 0) return null;
              const isExpanded = expandedMonths.includes(month);
              const hasActive = monthScenarios.some(s => s.id === activeScenarioId);

              return (
                <div key={month} style={{ marginBottom: 6 }}>
                  <div
                    onClick={() => toggleMonth(month)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                      padding: '4px 0',
                    }}
                  >
                    <span style={{ fontSize: 9, color: t.textMuted, transition: 'transform 0.15s', display: 'inline-block', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>&#9654;</span>
                    <span style={{ fontSize: 10, fontWeight: 600, color: hasActive ? greenDark : t.textPrimary, fontFamily: sans }}>
                      {formatMonth(month)}
                    </span>
                    <span style={{ fontSize: 9, color: t.textMuted, fontFamily: mono }}>
                      ({monthScenarios.length})
                    </span>
                  </div>

                  {isExpanded && (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', paddingLeft: 16, marginTop: 4 }}>
                      {monthScenarios.map(s => (
                        <div key={s.id} style={{
                          display: 'flex', alignItems: 'center', gap: 5,
                          padding: '4px 10px', borderRadius: 14,
                          border: `1.5px solid ${activeScenarioId === s.id ? green : t.border}`,
                          background: activeScenarioId === s.id ? greenLight : 'transparent',
                          cursor: 'pointer', transition: 'all 0.15s',
                        }}>
                          {renamingId === s.id ? (
                            <input
                              autoFocus
                              value={renameValue}
                              onChange={e => setRenameValue(e.target.value)}
                              onKeyDown={e => { if (e.key === 'Enter') renameScenario(s.id, renameValue); if (e.key === 'Escape') setRenamingId(null); }}
                              onBlur={() => renameScenario(s.id, renameValue)}
                              style={{
                                border: 'none', background: 'transparent', outline: 'none',
                                fontSize: 11, fontFamily: sans, fontWeight: 500, width: 100,
                                color: t.textPrimary,
                              }}
                            />
                          ) : (
                            <span
                              onClick={() => loadScenario(s.id)}
                              style={{ fontSize: 11, fontWeight: activeScenarioId === s.id ? 600 : 400, color: activeScenarioId === s.id ? greenDark : t.textSecondary, fontFamily: sans }}
                            >
                              {s.name}
                            </span>
                          )}
                          <svg onClick={(e) => { e.stopPropagation(); setRenamingId(s.id); setRenameValue(s.name); }} width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={t.textMuted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ cursor: 'pointer', opacity: 0.5, flexShrink: 0 }}>
                            <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                          </svg>
                          <svg onClick={(e) => { e.stopPropagation(); deleteScenario(s.id); }} width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={t.textMuted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ cursor: 'pointer', opacity: 0.5, flexShrink: 0 }}>
                            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                          </svg>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* RIGHT: scenario summary panel */}
          <div style={{
            flex: 1, background: t.bgSurface, border: `1px solid ${t.border}`, borderRadius: 10,
            padding: '12px 16px',
          }}>
            {activeScenarioId ? (() => {
              const activeScenario = savedScenarios.find(s => s.id === activeScenarioId);
              if (!activeScenario) return null;
              const scenarioForecast = applyCustomAdjustments(baseData, activeScenario.adjustments || []);
              const kpis = computeScenarioKPIs(baseData, scenarioForecast);
              const CATEGORY_LABELS = {
                payroll: 'Payroll', rdMfg: 'R&D Manufacturing', rdClinical: 'R&D Clinical',
                professional: 'Professional Services', facilities: 'Facilities', softwareIT: 'Software & IT',
                hrPeople: 'HR & People', depreciation: 'Depreciation', interest: 'Interest Income',
                grants: 'Grants', revenue: 'Revenue',
              };
              return (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: t.textPrimary, fontFamily: sans }}>{activeScenario.name}</span>
                    <span style={{ fontSize: 9, color: t.textMuted, fontFamily: mono }}>
                      {activeScenario.createdAt ? new Date(activeScenario.createdAt).toLocaleDateString() : ''}
                    </span>
                  </div>
                  {/* KPIs grid */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginBottom: 12 }}>
                    {kpis.map((kpi, i) => (
                      <div key={i} style={{
                        background: t.bg, borderRadius: 6, padding: '8px 10px',
                        border: `1px solid ${t.borderLight}`,
                      }}>
                        <div style={{ fontSize: 8, textTransform: 'uppercase', letterSpacing: 0.5, color: t.textMuted, fontWeight: 600, fontFamily: sans, marginBottom: 4 }}>
                          {kpi.label}
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, fontFamily: mono }}>
                          {kpi.isYear ? (kpi.scenario ? String(kpi.scenario) : 'None') : kpi.isMo ? (kpi.scenario >= 999 ? '∞' : `${kpi.scenario} mo`) : fmtCompactC(kpi.scenario, cc)}
                        </div>
                        {kpi.delta !== 0 && (
                          <div style={{ fontSize: 9, fontWeight: 600, fontFamily: mono, color: kpi.positive ? green : red }}>
                            {kpi.isYear
                              ? (kpi.delta > 0 ? `+${kpi.delta} yr` : `${kpi.delta} yr`)
                              : kpi.isMo
                                ? (kpi.delta > 0 ? `+${kpi.delta} mo` : `${kpi.delta} mo`)
                                : (kpi.delta > 0 ? '+' : '') + fmtCompactC(kpi.delta, cc)
                            } vs base
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  {/* Adjustments list */}
                  {activeScenario.adjustments && activeScenario.adjustments.length > 0 && (
                    <>
                      <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.5, color: t.textMuted, fontWeight: 600, fontFamily: sans, marginBottom: 6 }}>
                        Adjustments ({activeScenario.adjustments.length})
                      </div>
                      {activeScenario.adjustments.map((adj, j) => (
                        <div key={j} style={{
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          padding: '4px 8px', marginBottom: 2, borderRadius: 4,
                          background: t.bg, fontSize: 10, fontFamily: mono,
                        }}>
                          <span style={{ color: t.textPrimary, fontWeight: 500 }}>
                            {CATEGORY_LABELS[adj.category] || adj.category}
                          </span>
                          <span style={{
                            color: adj.action === 'reduce' ? red : green,
                            fontWeight: 600,
                          }}>
                            {adj.action === 'add' ? '+' : adj.action === 'reduce' ? '-' : 'x'}
                            {adj.amount >= 1000000 ? `$${(adj.amount/1000000).toFixed(1)}M` : adj.amount >= 1000 ? `$${(adj.amount/1000).toFixed(0)}K` : `$${adj.amount}`}
                            <span style={{ color: t.textMuted, fontWeight: 400 }}> from {adj.startYear}</span>
                          </span>
                        </div>
                      ))}
                    </>
                  )}
                </>
              );
            })() : (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                height: '100%', minHeight: 80,
                color: t.textMuted, fontSize: 11, fontFamily: sans, fontStyle: 'italic',
              }}>
                Select a scenario to view details
              </div>
            )}
          </div>
        </div>
        </>
      )}

      {/* Pending Review — scenario mode only */}
      {forecastMode === 'scenario' && pendingCommitments.length > 0 && (
        <div style={{ background: t.bgCard, border: `1px solid ${amber}22`, borderRadius: 10, padding: '14px 18px', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: amber, fontFamily: sans }}>
              Pending Review
            </span>
            <span style={{ fontSize: 10, background: amber + '22', color: amber, borderRadius: 10, padding: '1px 8px', fontWeight: 600, fontFamily: sans }}>
              {pendingCommitments.length}
            </span>
          </div>
          {pendingCommitments.map(c => (
            <div key={c.id} onClick={() => { setReviewingCommitment(c); setReviewGLAccount(c.glAccount || ''); setReviewRejecting(false); setReviewRejectReason(''); }}
              style={{ borderBottom: `1px solid ${t.borderLight}`, paddingBottom: 8, marginBottom: 8, cursor: 'pointer', borderRadius: 6, padding: '8px', transition: 'background 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.background = t.bgSurface}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: t.textPrimary, fontFamily: sans }}>{c.desc}</div>
                  <div style={{ fontSize: 9, color: t.textMuted, fontFamily: sans, marginTop: 1 }}>{c.dept} · {c.vendor} · {c.createdBy}</div>
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, color: t.textPrimary, fontFamily: mono }}>{fmtCompactC(c.amount, 'USD')}</span>
                <span style={{ fontSize: 9, color: t.textMuted, fontFamily: sans, padding: '3px 8px', borderRadius: 6, border: `1px solid ${t.border}` }}>Review →</span>
              </div>
            </div>
          ))}

          {/* Commitment Review Modal */}
          {reviewingCommitment && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              onClick={e => { if (e.target === e.currentTarget) { setReviewingCommitment(null); setReviewRejecting(false); } }}>
              <div style={{ background: t.bgCard, borderRadius: 14, padding: 28, width: 460, maxWidth: '95vw', boxShadow: '0 8px 40px rgba(0,0,0,0.18)', border: `1px solid ${t.border}` }}>
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: t.textPrimary, fontFamily: sans }}>{reviewingCommitment.desc}</div>
                    <div style={{ fontSize: 10, color: t.textMuted, fontFamily: sans, marginTop: 3 }}>Submitted by {reviewingCommitment.createdBy}</div>
                  </div>
                  <button onClick={() => { setReviewingCommitment(null); setReviewRejecting(false); }}
                    style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: t.textMuted, lineHeight: 1 }}>×</button>
                </div>

                {/* Details grid */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 20px', marginBottom: 18 }}>
                  {[
                    ['Vendor', reviewingCommitment.vendor || '—'],
                    ['Amount', fmtCompactC(reviewingCommitment.amount, 'USD')],
                    ['Department', reviewingCommitment.dept || '—'],
                    ['Expected Date', reviewingCommitment.date || '—'],
                    ['Recurrence', reviewingCommitment.recurrence || '—'],
                    ['Current GL', reviewingCommitment.glAccount || 'Not assigned'],
                  ].map(([label, value]) => (
                    <div key={label}>
                      <div style={{ fontSize: 9, fontWeight: 600, color: t.textMuted, fontFamily: sans, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>{label}</div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: t.textPrimary, fontFamily: label === 'Amount' ? mono : sans }}>{value}</div>
                    </div>
                  ))}
                </div>

                {/* GL Account override */}
                <div style={{ marginBottom: 18 }}>
                  <div style={{ fontSize: 9, fontWeight: 600, color: t.textMuted, fontFamily: sans, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>GL Account (override before approving)</div>
                  <input
                    type="text" placeholder="e.g. 6200 — Equipment" value={reviewGLAccount}
                    onChange={e => setReviewGLAccount(e.target.value)}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 7, border: `1px solid ${t.border}`, fontSize: 11, fontFamily: sans, background: t.bgPage, color: t.textPrimary, outline: 'none', boxSizing: 'border-box' }}
                  />
                </div>

                {/* Reject reason */}
                {reviewRejecting && (
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 9, fontWeight: 600, color: red, fontFamily: sans, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Rejection reason</div>
                    <input
                      type="text" placeholder="Explain why..." value={reviewRejectReason}
                      onChange={e => setReviewRejectReason(e.target.value)}
                      style={{ width: '100%', padding: '8px 10px', borderRadius: 7, border: `1px solid ${red}`, fontSize: 11, fontFamily: sans, background: t.bgPage, color: t.textPrimary, outline: 'none', boxSizing: 'border-box' }}
                    />
                  </div>
                )}

                {/* Actions */}
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  {!reviewRejecting ? (
                    <>
                      <button onClick={() => setReviewRejecting(true)}
                        style={{ padding: '8px 16px', borderRadius: 7, border: `1px solid ${red}`, background: 'transparent', color: red, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: sans }}>
                        Reject
                      </button>
                      <button onClick={() => handleApproveCommitment(reviewingCommitment.id, reviewGLAccount)}
                        style={{ padding: '8px 20px', borderRadius: 7, border: 'none', background: green, color: 'white', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: sans }}>
                        Approve
                      </button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => setReviewRejecting(false)}
                        style={{ padding: '8px 14px', borderRadius: 7, border: `1px solid ${t.border}`, background: 'transparent', color: t.textMuted, fontSize: 11, cursor: 'pointer', fontFamily: sans }}>
                        Cancel
                      </button>
                      <button onClick={() => handleRejectCommitment(reviewingCommitment.id, reviewRejectReason)}
                        style={{ padding: '8px 16px', borderRadius: 7, border: 'none', background: red, color: 'white', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: sans }}>
                        Confirm Rejection
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Meeting Modal */}
      {showMeetingModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={e => { if (e.target === e.currentTarget) { setShowMeetingModal(false); setMeetingMode('list'); } }}>
          <div style={{ background: t.bgCard, borderRadius: 14, padding: 28, width: 520, maxWidth: '95vw', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 8px 40px rgba(0,0,0,0.18)', border: `1px solid ${t.border}` }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {meetingMode !== 'list' && <button onClick={() => setMeetingMode('list')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: t.textMuted, fontSize: 13, padding: '0 4px' }}>← Back</button>}
                <span style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, fontFamily: sans }}>
                  {meetingMode === 'list' ? 'Meetings' : meetingMode === 'propose' ? 'Propose Time Slots' : 'Book Directly'}
                </span>
              </div>
              <button onClick={() => { setShowMeetingModal(false); setMeetingMode('list'); }} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: t.textMuted }}>×</button>
            </div>

            {/* List mode */}
            {meetingMode === 'list' && (
              <div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                  {!meetingGoogleToken ? (
                    <button onClick={meetingSignInGoogle} style={{ padding: '7px 14px', borderRadius: 7, border: `1px solid ${t.border}`, background: t.bgSurface, color: t.textPrimary, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: sans }}>
                      🔗 Connect Google Calendar
                    </button>
                  ) : (
                    <>
                      <button onClick={() => setMeetingMode('propose')} style={{ padding: '7px 14px', borderRadius: 7, border: `1px solid ${t.border}`, background: t.bgSurface, color: t.textPrimary, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: sans }}>Propose Slots</button>
                      <button onClick={() => { setMeetingMode('book'); fetchMeetingFreeSlots(meetingBookDate, meetingBookDuration, meetingGoogleToken); }} style={{ padding: '7px 14px', borderRadius: 7, border: 'none', background: green, color: 'white', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: sans }}>Book Directly</button>
                    </>
                  )}
                </div>
                {meetingProposals.length === 0 && <div style={{ textAlign: 'center', color: t.textMuted, fontSize: 12, fontFamily: sans, padding: '20px 0' }}>No meetings yet</div>}
                {meetingProposals.map(p => {
                  const sc = MEET_STATUS[p.status] || MEET_STATUS.pending;
                  return (
                    <div key={p.id} style={{ background: t.bgSurface, borderRadius: 8, padding: '10px 14px', marginBottom: 8, border: `1px solid ${t.borderLight}` }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: t.textPrimary, fontFamily: sans }}>{p.title}</span>
                        <span style={{ fontSize: 9, fontWeight: 600, background: sc.bg, color: sc.text, borderRadius: 8, padding: '2px 8px', fontFamily: sans }}>{sc.label}</span>
                      </div>
                      <div style={{ fontSize: 10, color: t.textMuted, fontFamily: sans }}>{p.department} · {p.lead_name} · {p.duration} min</div>
                      {p.selected_slot && <div style={{ fontSize: 10, color: green, fontFamily: sans, marginTop: 3 }}>📅 {fmtMeetSlot(p.selected_slot.start)}</div>}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Propose slots mode */}
            {meetingMode === 'propose' && (
              <div>
                {[['Title', 'title', 'text'], ['Lead Name', 'leadName', 'text'], ['Lead Email', 'leadEmail', 'email'], ['Context', 'context', 'text']].map(([label, key, type]) => (
                  <div key={key} style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 9, fontWeight: 600, color: t.textMuted, fontFamily: sans, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>{label}</div>
                    <input type={type} value={meetingForm[key]} onChange={e => setMeetingForm(prev => ({ ...prev, [key]: e.target.value }))}
                      style={{ width: '100%', padding: '8px 10px', borderRadius: 7, border: `1px solid ${t.border}`, fontSize: 11, fontFamily: sans, background: t.bgPage, color: t.textPrimary, outline: 'none', boxSizing: 'border-box' }} />
                  </div>
                ))}
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 9, fontWeight: 600, color: t.textMuted, fontFamily: sans, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Department</div>
                  <select value={meetingForm.department} onChange={e => setMeetingForm(prev => ({ ...prev, department: e.target.value }))}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 7, border: `1px solid ${t.border}`, fontSize: 11, fontFamily: sans, background: t.bgPage, color: t.textPrimary, outline: 'none' }}>
                    {MEETING_DEPTS.map(d => <option key={d}>{d}</option>)}
                  </select>
                </div>
                <div style={{ fontSize: 9, fontWeight: 600, color: t.textMuted, fontFamily: sans, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Proposed Slots</div>
                {meetingForm.slots.map((slot, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                    <input type="date" value={slot.date} onChange={e => { const s = [...meetingForm.slots]; s[i] = { ...s[i], date: e.target.value }; setMeetingForm(prev => ({ ...prev, slots: s })); }}
                      style={{ flex: 1, padding: '7px 10px', borderRadius: 7, border: `1px solid ${t.border}`, fontSize: 11, fontFamily: sans, background: t.bgPage, color: t.textPrimary, outline: 'none' }} />
                    <input type="time" value={slot.time} onChange={e => { const s = [...meetingForm.slots]; s[i] = { ...s[i], time: e.target.value }; setMeetingForm(prev => ({ ...prev, slots: s })); }}
                      style={{ flex: 1, padding: '7px 10px', borderRadius: 7, border: `1px solid ${t.border}`, fontSize: 11, fontFamily: sans, background: t.bgPage, color: t.textPrimary, outline: 'none' }} />
                  </div>
                ))}
                <button onClick={meetingSubmitProposal} disabled={meetingSaving}
                  style={{ width: '100%', padding: '10px', borderRadius: 8, border: 'none', background: green, color: 'white', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: sans, marginTop: 8 }}>
                  {meetingSaving ? 'Sending…' : 'Send Proposal'}
                </button>
              </div>
            )}

            {/* Book directly mode */}
            {meetingMode === 'book' && (
              <div>
                {[['Title', 'title', 'text'], ['Lead Name', 'leadName', 'text'], ['Lead Email', 'leadEmail', 'email'], ['Context', 'context', 'text']].map(([label, key, type]) => (
                  <div key={key} style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 9, fontWeight: 600, color: t.textMuted, fontFamily: sans, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>{label}</div>
                    <input type={type} value={meetingBookForm[key]} onChange={e => setMeetingBookForm(prev => ({ ...prev, [key]: e.target.value }))}
                      style={{ width: '100%', padding: '8px 10px', borderRadius: 7, border: `1px solid ${t.border}`, fontSize: 11, fontFamily: sans, background: t.bgPage, color: t.textPrimary, outline: 'none', boxSizing: 'border-box' }} />
                  </div>
                ))}
                <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 9, fontWeight: 600, color: t.textMuted, fontFamily: sans, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Date</div>
                    <input type="date" value={meetingBookDate} onChange={e => { setMeetingBookDate(e.target.value); fetchMeetingFreeSlots(e.target.value, meetingBookDuration, meetingGoogleToken); }}
                      style={{ width: '100%', padding: '7px 10px', borderRadius: 7, border: `1px solid ${t.border}`, fontSize: 11, fontFamily: sans, background: t.bgPage, color: t.textPrimary, outline: 'none' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 9, fontWeight: 600, color: t.textMuted, fontFamily: sans, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Duration</div>
                    <select value={meetingBookDuration} onChange={e => { setMeetingBookDuration(Number(e.target.value)); fetchMeetingFreeSlots(meetingBookDate, Number(e.target.value), meetingGoogleToken); }}
                      style={{ width: '100%', padding: '7px 10px', borderRadius: 7, border: `1px solid ${t.border}`, fontSize: 11, fontFamily: sans, background: t.bgPage, color: t.textPrimary, outline: 'none' }}>
                      {[15, 30, 45, 60].map(d => <option key={d} value={d}>{d} min</option>)}
                    </select>
                  </div>
                </div>
                {meetingLoadingSlots && <div style={{ textAlign: 'center', color: t.textMuted, fontSize: 11, fontFamily: sans, padding: '10px 0' }}>Loading slots…</div>}
                {meetingFreeSlots.length > 0 && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 9, fontWeight: 600, color: t.textMuted, fontFamily: sans, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Available Slots</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {meetingFreeSlots.map((slot, i) => (
                        <button key={i} onClick={() => setMeetingSelectedSlot(slot)}
                          style={{ padding: '5px 10px', borderRadius: 6, border: `1px solid ${meetingSelectedSlot?.start === slot.start ? green : t.border}`, background: meetingSelectedSlot?.start === slot.start ? greenLight : t.bgSurface, color: meetingSelectedSlot?.start === slot.start ? greenDark : t.textPrimary, fontSize: 10, fontFamily: sans, cursor: 'pointer', fontWeight: meetingSelectedSlot?.start === slot.start ? 600 : 400 }}>
                          {fmtMeetSlot(slot.start)}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <button onClick={meetingBookDirectly} disabled={meetingSaving || !meetingSelectedSlot}
                  style={{ width: '100%', padding: '10px', borderRadius: 8, border: 'none', background: meetingSelectedSlot ? green : t.border, color: 'white', fontSize: 12, fontWeight: 600, cursor: meetingSelectedSlot ? 'pointer' : 'default', fontFamily: sans, marginTop: 4 }}>
                  {meetingSaving ? 'Booking…' : 'Book & Send Invite'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Commitments Selector — scenario mode */}
      {forecastMode === 'scenario' && approvedCommitments.length > 0 && (
        <div style={{
          background: t.bgSurface, border: `1px solid ${t.border}`, borderRadius: 10,
          padding: '12px 16px', marginBottom: 12,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontSize: 10, fontWeight: 600, color: t.textMuted, fontFamily: sans, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Include Approved Commitments
            </span>
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => setSelectedCommitmentIds(new Set(approvedCommitments.map(c => c.id)))} style={{
                padding: '2px 8px', borderRadius: 8, fontSize: 9, fontFamily: sans,
                border: `1px solid ${t.border}`, background: 'transparent',
                color: t.textSecondary, cursor: 'pointer',
              }}>All</button>
              <button onClick={() => setSelectedCommitmentIds(new Set())} style={{
                padding: '2px 8px', borderRadius: 8, fontSize: 9, fontFamily: sans,
                border: `1px solid ${t.border}`, background: 'transparent',
                color: t.textSecondary, cursor: 'pointer',
              }}>None</button>
            </div>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {approvedCommitments.map(c => {
              const isSelected = selectedCommitmentIds.has(c.id);
              return (
                <button key={c.id} onClick={() => {
                  setSelectedCommitmentIds(prev => {
                    const next = new Set(prev);
                    isSelected ? next.delete(c.id) : next.add(c.id);
                    return next;
                  });
                }} style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '4px 10px', borderRadius: 14, cursor: 'pointer',
                  border: `1.5px solid ${isSelected ? green : t.border}`,
                  background: isSelected ? greenLight : 'transparent',
                  transition: 'all 0.15s',
                }}>
                  <span style={{ fontSize: 10, fontWeight: isSelected ? 600 : 400, color: isSelected ? greenDark : t.textSecondary, fontFamily: sans }}>
                    {c.desc}
                  </span>
                  <span style={{ fontSize: 9, color: isSelected ? green : t.textMuted, fontFamily: mono, fontWeight: 600 }}>
                    {c.amount >= 1000000 ? `$${(c.amount/1000000).toFixed(1)}M` : c.amount >= 1000 ? `$${(c.amount/1000).toFixed(0)}K` : `$${c.amount}`}
                  </span>
                  {c.glAccount && (
                    <span style={{ fontSize: 8, color: t.textFaint, fontFamily: mono, background: t.bgSurface, padding: '1px 4px', borderRadius: 4 }}>
                      GL {c.glAccount}
                    </span>
                  )}
                  {c.forecastCategory && (
                    <span style={{ fontSize: 8, color: isSelected ? green : t.textFaint, fontFamily: sans }}>
                      → {c.forecastCategory}
                    </span>
                  )}
                  {c.date && (
                    <span style={{ fontSize: 8, color: t.textFaint, fontFamily: mono }}>
                      {c.date.slice(0, 7)}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          {selectedCommitmentIds.size > 0 && (
            <div style={{ marginTop: 8, fontSize: 9, color: green, fontWeight: 600, fontFamily: mono }}>
              {selectedCommitmentIds.size} commitment{selectedCommitmentIds.size > 1 ? 's' : ''} included — total {(() => {
                const total = approvedCommitments.filter(c => selectedCommitmentIds.has(c.id)).reduce((s, c) => s + c.amount, 0);
                return total >= 1000000 ? `$${(total/1000000).toFixed(1)}M` : `$${(total/1000).toFixed(0)}K`;
              })()}
            </div>
          )}
        </div>
      )}

      {/* Scenario Builder Panel */}
      {forecastMode === 'scenario' && showScenarioPanel && <div style={{
        background: t.bgSurface, border: `1px solid ${t.border}`, borderRadius: 10,
        padding: showScenarioPanel ? '16px 20px' : '10px 20px', marginBottom: 16,
        transition: 'all 0.2s',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => setShowScenarioPanel(!showScenarioPanel)}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: t.textPrimary, fontFamily: sans }}>Scenario Builder</span>
            {isComparing && (
              <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, background: amberLight, color: amberDark, fontWeight: 600, fontFamily: sans }}>
                {scenarioLabel}
              </span>
            )}
          </div>
          <span style={{ fontSize: 10, color: t.textMuted }}>{showScenarioPanel ? '\u25B2' : '\u25BC'}</span>
        </div>

        {showScenarioPanel && (
          <div style={{ marginTop: 14 }}>
            {/* AI Scenario Chat — primary */}
            <div style={{ marginBottom: 16 }}>
              <ScenarioChat forecastData={displayData} customAdjustments={customAdjustments} onApplyAdjustments={(adjs) => {
                adjs.forEach(adj => {
                  setCustomAdjustments(prev => [...prev, {
                    category: adj.category,
                    action: adj.action,
                    amount: adj.amount,
                    startYear: adj.start_year,
                  }]);
                });
              }} />
            </div>

            {/* Custom adjustments */}
            <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.5, color: t.textMuted, fontWeight: 600, marginBottom: 6, fontFamily: sans }}>
              Custom Adjustments {customAdjustments.length > 0 && `(${customAdjustments.length})`}
            </div>

            {/* Column headers */}
            {customAdjustments.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px 100px 80px 28px', gap: 6, marginBottom: 4 }}>
                <span style={{ fontSize: 9, color: t.textMuted, fontWeight: 600, textTransform: 'uppercase', fontFamily: sans }}>Category</span>
                <span style={{ fontSize: 9, color: t.textMuted, fontWeight: 600, textTransform: 'uppercase', fontFamily: sans }}>Action</span>
                <span style={{ fontSize: 9, color: t.textMuted, fontWeight: 600, textTransform: 'uppercase', fontFamily: sans }}>Amount</span>
                <span style={{ fontSize: 9, color: t.textMuted, fontWeight: 600, textTransform: 'uppercase', fontFamily: sans }}>From</span>
                <span />
              </div>
            )}

            {customAdjustments.map((adj, i) => (
              <AdjustmentRow key={i} adj={adj} onChange={a => updateAdjustment(i, a)} onRemove={() => removeAdjustment(i)} theme={t} />
            ))}

            {customAdjustments.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
                <button onClick={() => setCustomAdjustments([])} style={{
                  padding: '6px 14px', borderRadius: 6, fontSize: 11, fontFamily: sans,
                  border: `1px solid ${red}40`, background: red + '08',
                  color: red, cursor: 'pointer', fontWeight: 500,
                }}>
                  Clear all
                </button>
              </div>
            )}

          </div>
        )}
      </div>}

      {/* Scenario Impact KPIs */}
      {forecastMode === 'scenario' && customAdjustments.length > 0 && (() => {
        const currentYear = new Date().getFullYear();
        const baseYearData = baseData.find(d => d.year === currentYear) || baseData[0];
        const scenarioYearData = displayData.find(d => d.year === currentYear) || displayData[0];

        const baseBurnMonthly = Math.round(baseYearData.netBurn / 12);
        const scenarioBurnMonthly = Math.round(scenarioYearData.netBurn / 12);
        const burnDelta = scenarioBurnMonthly - baseBurnMonthly;

        const baseRunway = baseBurnMonthly > 0 ? Math.round(baseYearData.cashEnd / baseBurnMonthly) : 999;
        const scenarioRunway = scenarioBurnMonthly > 0 ? Math.round(scenarioYearData.cashEnd / scenarioBurnMonthly) : 999;
        const runwayDelta = scenarioRunway - baseRunway;

        const baseCashEnd = baseYearData.cashEnd;
        const scenarioCashEnd = scenarioYearData.cashEnd;
        const cashDelta = scenarioCashEnd - baseCashEnd;

        const baseCashout = baseData.find(d => d.cashEnd <= 0)?.year || null;
        const scenarioCashout = displayData.find(d => d.cashEnd <= 0)?.year || null;

        const kpis = [
          {
            label: 'Monthly Burn',
            base: fmtCompactC(baseBurnMonthly, cc),
            scenario: fmtCompactC(scenarioBurnMonthly, cc),
            delta: burnDelta,
            fmtDelta: (burnDelta > 0 ? '+' : '') + fmtCompactC(burnDelta, cc),
            positive: burnDelta <= 0, // less burn is good
          },
          {
            label: 'Cash Runway',
            base: baseRunway >= 999 ? '∞' : `${baseRunway} mo`,
            scenario: scenarioRunway >= 999 ? '∞' : `${scenarioRunway} mo`,
            delta: runwayDelta,
            fmtDelta: (runwayDelta > 0 ? '+' : '') + `${runwayDelta} mo`,
            positive: runwayDelta >= 0, // more runway is good
          },
          {
            label: `Cash End ${currentYear}`,
            base: fmtCompactC(baseCashEnd, cc),
            scenario: fmtCompactC(scenarioCashEnd, cc),
            delta: cashDelta,
            fmtDelta: (cashDelta > 0 ? '+' : '') + fmtCompactC(cashDelta, cc),
            positive: cashDelta >= 0, // more cash is good
          },
          {
            label: 'Cash-out Year',
            base: baseCashout ? String(baseCashout) : 'None',
            scenario: scenarioCashout ? String(scenarioCashout) : 'None',
            delta: baseCashout && scenarioCashout ? scenarioCashout - baseCashout : 0,
            fmtDelta: !scenarioCashout ? 'No risk' : baseCashout ? (scenarioCashout > baseCashout ? `+${scenarioCashout - baseCashout} yr` : `${scenarioCashout - baseCashout} yr`) : scenarioCashout ? String(scenarioCashout) : '—',
            positive: !scenarioCashout || (baseCashout && scenarioCashout > baseCashout),
          },
        ];

        return (
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 16,
          }}>
            {kpis.map((kpi, i) => (
              <div key={i} style={{
                background: t.bgSurface, border: `1px solid ${t.border}`, borderRadius: 8,
                padding: '10px 14px',
              }}>
                <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.5, color: t.textMuted, fontWeight: 600, fontFamily: sans, marginBottom: 6 }}>
                  {kpi.label}
                </div>
                <div style={{ fontSize: 16, fontWeight: 700, color: t.textPrimary, fontFamily: mono, marginBottom: 2 }}>
                  {kpi.scenario}
                </div>
                {kpi.delta !== 0 && (
                  <div style={{
                    fontSize: 10, fontWeight: 600, fontFamily: mono,
                    color: kpi.positive ? green : red,
                  }}>
                    {kpi.fmtDelta} vs base
                  </div>
                )}
                {kpi.delta === 0 && (
                  <div style={{ fontSize: 10, color: t.textMuted, fontFamily: mono }}>
                    No change
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      })()}

      {/* Cash Runway Alert — removed per user (cashflow chart shows the trough) */}

      {/* Annual P&L Forecast — editable, multi-view */}
      <AnnualPLForecast />

    </div>
  );
}

// ─── Commitments Panel (connected to Supabase) ──────────────────────────────
function CommitmentsPanel({ t }) {
  const tc = useTheme();
  const [liveCommitments, setLiveCommitments] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchCommitments = useCallback(async () => {
    try {
      const { data, error } = await supabaseCommit.from('commitments').select('*').order('created_at', { ascending: false });
      if (error) console.error('Commitments fetch error:', error);
      if (data) {
        setLiveCommitments(data.map(c => ({
          id: c.id, dept: c.dept, desc: c.description, vendor: c.vendor || '—',
          amount: Number(c.amount), date: c.expected_date, recurrence: c.recurrence,
          status: c.status, createdBy: c.created_by_name || 'Unknown',
          rejectionReason: c.rejection_reason || '',
        })));
      }
    } catch (err) {
      console.error('Commitments fetch exception:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCommitments();
    const channel = supabaseCommit.channel('commitments-web')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'commitments' }, () => fetchCommitments())
      .subscribe();
    return () => { supabaseCommit.removeChannel(channel); };
  }, [fetchCommitments]);

  const handleApprove = async (id) => {
    await supabaseCommit.from('commitments').update({ status: 'committed', approved_at: new Date().toISOString() }).eq('id', id);
    fetchCommitments();
  };

  const handleRevert = async (id) => {
    await supabaseCommit.from('commitments').update({ status: 'confirmed', approved_at: null }).eq('id', id);
    fetchCommitments();
  };

  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const handleReject = async (id, reason) => {
    // Try with rejection_reason, fallback without if schema not ready
    let { error } = await supabaseCommit.from('commitments').update({ status: 'rejected', rejection_reason: reason || '' }).eq('id', id);
    if (error && error.message.includes('rejection_reason')) {
      // Fallback: store reason in notes or just update status
      ({ error } = await supabaseCommit.from('commitments').update({ status: 'rejected' }).eq('id', id));
      if (error) console.error('Reject error:', error);
    }
    setRejectingId(null);
    setRejectReason('');
    fetchCommitments();
  };

  const confirmedItems = liveCommitments.filter(c => c.status === 'confirmed');
  const committedItems = liveCommitments.filter(c => c.status === 'committed');
  const totalPending = confirmedItems.reduce((s, c) => s + c.amount, 0);
  const totalApproved = committedItems.reduce((s, c) => s + c.amount, 0);

  if (loading) return <div style={{ textAlign: 'center', padding: 40, color: tc.textMuted, fontFamily: sans }}>Loading commitments...</div>;

  return (
    <div>
      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 16 }}>
        <div style={{ background: tc.bgSurface, border: `1px solid ${tc.border}`, borderRadius: 8, padding: '12px 16px' }}>
          <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.5, color: tc.textMuted, fontWeight: 600, fontFamily: sans, marginBottom: 4 }}>Pending Approval</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: amber, fontFamily: mono }}>{fmtCompactC(totalPending, 'USD')}</div>
          <div style={{ fontSize: 10, color: tc.textMuted, fontFamily: sans, marginTop: 2 }}>{confirmedItems.length} commitments</div>
        </div>
        <div style={{ background: tc.bgSurface, border: `1px solid ${tc.border}`, borderRadius: 8, padding: '12px 16px' }}>
          <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.5, color: tc.textMuted, fontWeight: 600, fontFamily: sans, marginBottom: 4 }}>Approved</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: green, fontFamily: mono }}>{fmtCompactC(totalApproved, 'USD')}</div>
          <div style={{ fontSize: 10, color: tc.textMuted, fontFamily: sans, marginTop: 2 }}>{committedItems.length} commitments</div>
        </div>
        <div style={{ background: tc.bgSurface, border: `1px solid ${tc.border}`, borderRadius: 8, padding: '12px 16px' }}>
          <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.5, color: tc.textMuted, fontWeight: 600, fontFamily: sans, marginBottom: 4 }}>Total Forecast Impact</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: tc.textPrimary, fontFamily: mono }}>{fmtCompactC(totalPending + totalApproved, 'USD')}</div>
          <div style={{ fontSize: 10, color: tc.textMuted, fontFamily: sans, marginTop: 2 }}>{liveCommitments.length} total</div>
        </div>
      </div>

      {/* Pending Approval Section */}
      <div style={{ background: tc.bgCard, borderRadius: 10, padding: '16px 20px', border: `1px solid ${tc.border}`, marginBottom: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: amber, marginBottom: 12, fontFamily: sans }}>
          Pending Approval ({confirmedItems.length})
        </div>
        {confirmedItems.length === 0 && <div style={{ fontSize: 11, color: tc.textMuted, fontFamily: sans, padding: '8px 0' }}>No pending commitments</div>}
        {confirmedItems.map(c => (
          <div key={c.id} style={{ padding: '10px 0', borderBottom: `1px solid ${tc.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: tc.textPrimary, fontFamily: sans }}>{c.desc}</div>
                <div style={{ fontSize: 10, color: tc.textMuted, fontFamily: sans, marginTop: 2 }}>
                  {c.dept} · {c.vendor} · by {c.createdBy} · {c.date}
                </div>
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, color: tc.textPrimary, fontFamily: mono, marginRight: 12 }}>
                {fmtCompactC(c.amount, 'USD')}
              </div>
              <button onClick={() => handleApprove(c.id)} style={{
                padding: '4px 12px', borderRadius: 6, border: 'none',
                background: green, color: 'white', fontSize: 10, fontWeight: 600,
                cursor: 'pointer', fontFamily: sans, marginRight: 6,
              }}>
                Approve
              </button>
              <button onClick={() => setRejectingId(rejectingId === c.id ? null : c.id)} style={{
                padding: '4px 12px', borderRadius: 6, border: `1px solid ${red}`,
                background: rejectingId === c.id ? red + '12' : 'transparent', color: red, fontSize: 10, fontWeight: 600,
                cursor: 'pointer', fontFamily: sans,
              }}>
                Reject
              </button>
            </div>
            {rejectingId === c.id && (
              <div style={{ display: 'flex', alignItems: 'center', padding: '8px 0', marginTop: 4 }}>
                <input
                  type="text"
                  placeholder="Reason for rejection..."
                  value={rejectReason}
                  onChange={e => setRejectReason(e.target.value)}
                  style={{
                    flex: 1, padding: '6px 10px', borderRadius: 6, border: `1px solid ${tc.border}`,
                    fontSize: 11, fontFamily: sans, color: tc.textPrimary, background: tc.inputBg || tc.bgSurface,
                    marginRight: 8, outline: 'none',
                  }}
                />
                <button onClick={() => handleReject(c.id, rejectReason)} style={{
                  padding: '5px 12px', borderRadius: 6, border: 'none',
                  background: red, color: 'white', fontSize: 10, fontWeight: 600,
                  cursor: 'pointer', fontFamily: sans, marginRight: 6,
                }}>
                  Confirm
                </button>
                <button onClick={() => { setRejectingId(null); setRejectReason(''); }} style={{
                  padding: '5px 10px', borderRadius: 6, border: `1px solid ${tc.border}`,
                  background: 'transparent', color: tc.textMuted, fontSize: 10,
                  cursor: 'pointer', fontFamily: sans,
                }}>
                  Cancel
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Approved Section */}
      <div style={{ background: tc.bgCard, borderRadius: 10, padding: '16px 20px', border: `1px solid ${tc.border}` }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: green, marginBottom: 12, fontFamily: sans }}>
          ✓ Approved ({committedItems.length})
        </div>
        {committedItems.length === 0 && <div style={{ fontSize: 11, color: tc.textMuted, fontFamily: sans, padding: '8px 0' }}>No approved commitments yet</div>}
        {committedItems.map(c => (
          <div key={c.id} style={{ display: 'flex', alignItems: 'center', padding: '10px 0', borderBottom: `1px solid ${tc.border}` }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: tc.textPrimary, fontFamily: sans }}>{c.desc}</div>
              <div style={{ fontSize: 10, color: tc.textMuted, fontFamily: sans, marginTop: 2 }}>
                {c.dept} · {c.vendor} · by {c.createdBy} · {c.date}
              </div>
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: green, fontFamily: mono, marginRight: 12 }}>
              {fmtCompactC(c.amount, 'USD')}
            </div>
            <button onClick={() => handleRevert(c.id)} style={{
              padding: '4px 10px', borderRadius: 6, border: `1px solid ${tc.border}`,
              background: 'transparent', color: tc.textMuted, fontSize: 9, fontWeight: 600,
              cursor: 'pointer', fontFamily: sans,
            }}>
              Revert to Pending
            </button>
          </div>
        ))}
      </div>

      {/* Rejected Section */}
      {liveCommitments.filter(c => c.status === 'rejected').length > 0 && (
      <div style={{ background: tc.bgCard, borderRadius: 10, padding: '16px 20px', border: `1px solid ${tc.border}`, marginTop: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: red, marginBottom: 12, fontFamily: sans }}>
          ✗ Rejected ({liveCommitments.filter(c => c.status === 'rejected').length})
        </div>
        {liveCommitments.filter(c => c.status === 'rejected').map(c => (
          <div key={c.id} style={{ padding: '10px 0', borderBottom: `1px solid ${tc.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: tc.textMuted, fontFamily: sans, textDecoration: 'line-through' }}>{c.desc}</div>
                <div style={{ fontSize: 10, color: tc.textMuted, fontFamily: sans, marginTop: 2 }}>
                  {c.dept} · {c.vendor} · by {c.createdBy} · {c.date}
                </div>
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, color: tc.textMuted, fontFamily: mono, marginRight: 12 }}>
                {fmtCompactC(c.amount, 'USD')}
              </div>
              <button onClick={() => handleRevert(c.id)} style={{
                padding: '4px 10px', borderRadius: 6, border: `1px solid ${tc.border}`,
                background: 'transparent', color: tc.textMuted, fontSize: 9, fontWeight: 600,
                cursor: 'pointer', fontFamily: sans,
              }}>
                Revert to Pending
              </button>
            </div>
            {c.rejectionReason && (
              <div style={{ fontSize: 10, color: red, fontStyle: 'italic', fontFamily: sans, marginTop: 4, paddingLeft: 8, borderLeft: `2px solid ${red}30` }}>
                Reason: {c.rejectionReason}
              </div>
            )}
          </div>
        ))}
      </div>
      )}
    </div>
  );
}
